// Reports — revenue rollups + CSV downloads.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Download, DollarSign, BarChart3 } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const usd = (n) => `$${Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function AdminReports() {
    const [revenue, setRevenue] = useState({ by_month: [], by_room: [] });
    const [loading, setLoading] = useState(true);
    const [dl, setDl] = useState(false);
    const [range, setRange] = useState({
        from: new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10),
        to: new Date().toISOString().slice(0, 10),
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await adminApi.get("/admin/reports/revenue");
                setRevenue(data);
            } catch { toast.error("Could not load revenue"); }
            finally { setLoading(false); }
        })();
    }, []);

    const download = async () => {
        setDl(true);
        try {
            const params = new URLSearchParams();
            if (range.from) params.set("check_in_from", range.from);
            if (range.to) params.set("check_in_to", range.to);
            const res = await adminApi.get(`/admin/reports/reservations.csv?${params}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `reservations-${range.from}_to_${range.to}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch { toast.error("Could not download CSV"); }
        finally { setDl(false); }
    };

    const maxRev = Math.max(1, ...revenue.by_month.map((m) => m.revenue));
    const maxRoomRev = Math.max(1, ...revenue.by_room.map((r) => r.revenue));

    return (
        <div data-testid="admin-reports-page">
            <header className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Insights</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Reports & exports</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">Revenue by month and by room, and downloadable CSVs for accounting.</p>
            </header>

            {/* CSV Export */}
            <section className="bg-white border border-navy/10 rounded-2xl p-6 mb-8">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60">CSV export</div>
                        <div className="font-display text-2xl text-navy mt-1">Reservations report</div>
                        <p className="text-[12px] text-navy/60 mt-1">Every reservation whose check-in falls in the selected range.</p>
                    </div>
                    <div className="flex items-end gap-3 flex-wrap">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">From</span>
                            <input data-testid="csv-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1.5 border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">To</span>
                            <input data-testid="csv-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1.5 border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                        <button onClick={download} disabled={dl} data-testid="csv-download" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-ivory text-[11px] uppercase tracking-[0.16em]">
                            <Download size={13} /> {dl ? "Downloading…" : "Download CSV"}
                        </button>
                    </div>
                </div>
            </section>

            {loading ? <div className="text-navy/60 text-sm">Loading revenue…</div> : (
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Revenue by month */}
                    <section className="bg-white border border-navy/10 rounded-2xl p-6" data-testid="revenue-by-month">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Monthly</div>
                                <div className="font-display text-2xl text-navy mt-1">Revenue by month</div>
                            </div>
                            <TrendingUp size={18} className="text-gold" />
                        </div>
                        <div className="space-y-2">
                            {revenue.by_month.length === 0 && <div className="text-sm text-navy/50">No revenue recorded yet.</div>}
                            {revenue.by_month.slice().reverse().map((m) => (
                                <div key={m.month} className="flex items-center gap-3 text-sm">
                                    <div className="w-16 text-navy/60 text-[11px]">{m.month}</div>
                                    <div className="flex-1 h-6 bg-navy/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-jade to-gold" style={{ width: `${(m.revenue / maxRev) * 100}%` }} />
                                    </div>
                                    <div className="w-24 text-right font-display text-navy">{usd(m.revenue)}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Revenue by room */}
                    <section className="bg-white border border-navy/10 rounded-2xl p-6" data-testid="revenue-by-room">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Room performance</div>
                                <div className="font-display text-2xl text-navy mt-1">Revenue by room</div>
                            </div>
                            <BarChart3 size={18} className="text-gold" />
                        </div>
                        <div className="space-y-2">
                            {revenue.by_room.length === 0 && <div className="text-sm text-navy/50">No revenue recorded yet.</div>}
                            {revenue.by_room.slice(0, 10).map((r) => (
                                <div key={r.slug || r.name} className="flex items-center gap-3 text-sm">
                                    <div className="w-56 truncate text-navy/70">{r.name}</div>
                                    <div className="flex-1 h-6 bg-navy/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-navy to-jade" style={{ width: `${(r.revenue / maxRoomRev) * 100}%` }} />
                                    </div>
                                    <div className="w-14 text-right text-[11px] text-navy/50">{r.count}×</div>
                                    <div className="w-24 text-right font-display text-navy inline-flex items-center justify-end gap-0.5">
                                        <DollarSign size={12} className="text-gold" />{usd(r.revenue).replace("$", "")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
